<template>
  <div :class="reportClasses">
    <link
      rel="stylesheet"
      :href="`https://fonts.googleapis.com/css?family=${fontList}&display=swap`"
    />
    <div class="topbar" v-if="topbar">
      <div class="lang-select" v-if="$store.getters.multilingual">
        <select v-model="currentLanguage">
          <option
            :key="lang.value"
            :value="lang.value"
            v-for="lang in $store.state.supportedLanguages"
            >{{ lang.label }}</option
          >
        </select>
      </div>
    </div>
    <div :class="['component-wrapper', reordering ? 'reordering' : '']">
      <component
        :data-index="0"
        ref="headerItem"
        :class="reportItemClasses(firstItem)"
        v-bind:report="report"
        v-bind:response="response"
        @add="onAdd(1)"
        @edit="onEdit"
        :editable="editable"
        :key="`item_${firstItem.key}`"
        :config="firstItem"
        v-bind:is="firstItem.type"
      ></component>
      <draggable
        handle=".handle"
        @change="onReordered"
        @choose="onStartReorder"
        @end="onEndReorder"
        v-model="reorderableComponents"
      >
        <transition-group name="report-item">
          <component
            :data-index="i + 1"
            ref="orderableItems"
            :class="reportItemClasses(item)"
            v-bind:report="report"
            v-bind:response="response"
            @add="onAdd(i + 2)"
            @delete="onDelete(i + 1)"
            @edit="onEdit"
            :editable="editable"
            :key="`item_${item.key}`"
            v-for="(item, i) in reorderableComponents"
            :config="item"
            v-bind:is="item.type"
          >
          </component>
        </transition-group>
      </draggable>
      <component
        :data-index="this.config.items.length - 1"
        ref="footerItem"
        :class="reportItemClasses(lastItem)"
        v-bind:report="report"
        v-bind:response="response"
        @edit="onEdit"
        :editable="editable"
        :key="`item_${lastItem.key}`"
        :config="lastItem"
        v-bind:is="lastItem.type"
      ></component>
    </div>
    <transition name="fade-without-move">
      <div key="modal" class="modal-bg" v-show="showSignupModal">
        <LeadForm
          :config="config.signup"
          :editable="editable"
          @signup="signup($event)"
          @close="hideSignup"
        ></LeadForm>
      </div>
    </transition>
  </div>
</template>
<script>
import draggable from "vuedraggable";
import ReportEvents from "./lib/ReportEvents";
import Heading from "./components/Heading";
import TextBox from "./components/TextBox";
import Graphic from "./components/Graphic";
import ImageBox from "./components/ImageBox";
import StatGroup from "./components/StatGroup";
import Stylesheet from "../lib/Stylesheet";
import AgreementGraph from "./components/AgreementGraph";
import ExpandingStat from "./components/ExpandingStat";
import HeadlineStatistic from "./components/HeadlineStatistic";
import Footer from "./components/Footer";
import ViewMixin from "./../mixins/ViewMixin";
import SaveMixin from "./../mixins/SaveMixin";
import LeadForm from "./components/LeadForm";
import FeaturedQuestion from "./components/FeaturedQuestion";
import { CookieStorage } from "cookie-storage";
import Vue from "vue";

export default {
  mixins: [ViewMixin, SaveMixin],
  props: {
    topbar: { type: Boolean, default: true },
    uid: { type: String },
    cid: { type: String },
    lead: { type: String, default: null },
    config: {
      type: Object,
      default: () => {
        {
        }
      },
    },
    answers: { type: Object, default: null },
    response: {
      type: Object,
      default: () => {
        {
        }
      },
    },
    supportedLanguages: {
      type: Array,
      default: () => {
        [];
      },
    },
    editable: { default: false, type: Boolean },
    published: { type: Boolean, default: true },
    locked: { type: Boolean, default: false },
  },
  data() {
    return {
      reordering: false,
      leadUUID: null,
      showSignupModal: false,
      localConfig: {},
      selectedFonts: { headingFont: "", copyFont: "" },
      brandColors: { backgroundColor: "", color: "" },
      selectedTheme: "",
      brandStyles: null,
      report: {
        stats: {},
        questions: {},
      },
      windowWidth: null,
    };
  },
  components: {
    Heading,
    TextBox,
    StatGroup,
    AgreementGraph,
    ExpandingStat,
    HeadlineStatistic,
    Footer,
    LeadForm,
    ImageBox,
    FeaturedQuestion,
    Graphic,
    draggable,
  },
  computed: {
    firstItem() {
      return this.config.items[0];
    },
    lastItem() {
      return this.config.items[this.config.items.length - 1];
    },
    reorderableComponents: {
      get: function() {
        let copy = Array.from(this.displayableComponents);
        copy.pop();
        copy.shift();
        return copy;
      },
      set: function(newItemList) {
        let firstItem = this.config.items[0];
        let lastItem = this.config.items[this.config.items.length - 1];
        this.config.items = [firstItem].concat(newItemList).concat(lastItem);
      },
    },
    displayableComponents: {
      get: function() {
        return this.config.items.filter((item) => {
          if (this.published == true) {
            if (item.type === "AgreementGraph" && !this.response) {
              return false;
            } else {
              return true;
            }
          }
          return true;
        });
      },
      set: function(newItemList) {
        this.config.items = newItemList;
      },
    },
    currentLanguage: {
      get() {
        return this.$store.state.language;
      },
      set(lang) {
        this.$store.commit("setLanguage", { language: lang });
      },
    },
    reportClasses() {
      let classes = ["report", `lang-${this.$store.state.language}`];
      if (this.leadUUID) {
        classes.push("user");
      } else {
        classes.push("anon");
      }
      if (this.reordering) {
        classes.push("reordering");
      }
      if (this.locked) {
        classes.push("locked");
      }
      let themeClass = this.selectedTheme
        ? this.selectedTheme.toLowerCase()
        : "light";
      classes.push(themeClass);
      return classes.join(" ");
    },
    headingFont() {
      return this.config.theme.headingFont.replace(/\s/g, "+");
    },
    fontList() {
      let fonts = [];
      if (this.selectedFonts.headingFont !== "") {
        fonts.push(this.escapeFontName(this.selectedFonts.headingFont));
      }
      if (this.selectedFonts.copyFont !== "") {
        fonts.push(this.escapeFontName(this.selectedFonts.copyFont));
      }
      return fonts.join(":wght@0,400;1,700&family=") + ":wght@0,400;1,700";
    },
  },
  methods: {
    onReordered(event) {
      let oldIndex = event.moved.oldIndex + 1;
      let newIndex = event.moved.newIndex + 1;
      this.$emit("reordered", { oldIndex, newIndex });
    },
    enableReorder(tgt) {
      tgt.$el.classList.add("sortable-selected");
      this.reordering = true;
    },
    onStartReorder(event) {},
    onEndReorder(event) {
      this.reordering = false;
      document.querySelectorAll(".sortable-selected").forEach((item) => {
        item.classList.remove("sortable-selected");
      });
    },
    reportItemClasses(item, options) {
      if (typeof options === "undefined") {
        options = {};
      }
      let key = "";
      if (typeof item.key !== "undefined") {
        key = item.key;
      }
      let baseClass = `${item.type}-${key}`.toLowerCase();
      return `${baseClass}`;
    },
    showSignup() {
      this.showSignupModal = true;
    },
    hideSignup() {
      this.showSignupModal = false;
    },
    signup(event) {
      this.createSignup(event)
        .then((resp) => {
          this.leadUUID = resp.data.uuid;
          this.cookies.setItem(this.reportCookieKey, this.leadUUID.toString());
          this.hideSignup();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    postViewEvent() {
      let viewEvent = {
        event: "view_report",
        label: "",
        src: document.location.href,
        context: {},
      };
      this.postEvent(viewEvent).catch((err) => {
        console.log(err);
      });
    },
    shouldDisplayComponent(item) {
      if (this.published === true) {
        if (item.type === "AgreementGraph" && !this.response) {
          return false;
        }
      }
      return true;
    },
    updateStats(stats) {
      this.report.stats = stats;
    },
    createSignup(formData) {
      let reportVars = {
        source_uid: this.uid,
        source_type: "report",
        src: document.location.href,
        event: "signup",
      };
      formData = Object.assign(formData, reportVars);
      if (!this.published) {
        return;
      }
      let promise = this.post(`/leads/${this.cid}.js`, formData);
      return promise;
    },
    postEvent(formData) {
      if (location.search.match(/preview=true/)) {
        console.log("Previewing report");
        return new Promise(function(resolve, reject) {
          resolve();
        });
      }
      let reportVars = {
        source_uid: this.uid,
        source_type: "report",
      };

      if (this.leadUUID) {
        reportVars.leaduuid = this.leadUUID;
      }

      if (this.response) {
        reportVars.uuid = this.response.uuid;
      }

      formData = Object.assign(formData, reportVars);
      if (!this.published) {
        return;
      }

      // let promise = this.post('/events.js', formData)
      let promise = this.post(`/leads/${this.cid}.js`, formData);
      return promise;
    },
    randKey() {
      let key = [...Array(10)]
        .map((i) => (~~(Math.random() * 36)).toString(36))
        .join("");
      return key;
    },
    onResize() {
      this.windowWidth = document.body.clientWidth;
    },
    escapeFontName(name) {
      return name.replace(/\s/g, "+");
    },
    onEdit(tgt) {
      this.$emit("edit", tgt);
    },
    onAdd(index) {
      this.$emit("add", index);
    },
    onDelete(index) {
      this.$emit("delete", index);
    },
    onCTAClick(event) {
      let formData = {
        event: event.name,
        src: document.location.href,
        label: event.label,
      };
      if (typeof event.context !== "undefined") {
        formData.context = event.context;
      }
      this.postEvent(formData).catch((err) => {
        console.log(err);
      });
    },
    fontsHaveChanged(newTheme) {
      return (
        newTheme.headingFont !== this.selectedFonts.headingFont ||
        newTheme.copyFont !== this.selectedFonts.copyFont
      );
    },
    themeHasChanged(newTheme) {
      return newTheme.name !== this.selectedTheme;
    },
    brandColoursHaveChanged(newTheme) {
      return (
        newTheme.brandColor !== this.brandColors.color ||
        newTheme.brandTextColor !== this.brandColors.backgroundColor
      );
    },
    reloadBrandStyles() {
      if (!this.brandStyles) {
        this.brandStyles = new Stylesheet("cc_brand");
      }
      this.brandStyles.clear();
      this.selectedFonts.headingFont = this.localConfig.theme.headingFont;
      this.selectedFonts.copyFont = this.localConfig.theme.copyFont;
      let fontDefinitions = {
        headingFont: {
          selector: ".report h1,h2,h3,h4,h5",
          family: this.selectedFonts.headingFont,
        },
        copyFont: {
          selector: ".report",
          family: this.selectedFonts.copyFont,
        },
      };
      this.brandStyles.addFonts(fontDefinitions);
      this.brandColors.backgroundColor = this.localConfig.theme.brandColor;
      this.brandColors.textColor = this.localConfig.theme.brandTextColor;
      this.brandColors.titleColor = this.localConfig.theme.brandTitleColor;
      this.brandStyles.addRule(".report .brand-background-color", {
        backgroundColor: this.brandColors.backgroundColor,
      });
      this.brandStyles.addRule(".report .brand-title-color", {
        color: this.brandColors.titleColor,
      });
      this.brandStyles.addRule(".report .brand-text-color", {
        color: this.brandColors.textColor,
      });
    },
    reloadBaseStyles() {
      // if (this.themeStyle) {
      //   Stylesheet.removeLink(this.themeStyle.id)
      // }
      // this.themeStyle = Stylesheet.addLink('/reports/themes/' + this.localConfig.theme.name.toLowerCase() + '.css')
      this.selectedTheme = this.localConfig.theme.name;
    },
    onDOMReady() {
      if (this.isTouch()) {
        document.body.classList.add("touch");
      }
    },
  },
  provide() {
    return {
      globalTheme: this.config.theme,
    };
  },
  created() {
    if (typeof cogniclick !== "undefined") {
      let language = cogniclick.country === "GB" ? "EN" : cogniclick.country;
      this.$store.commit("setLanguage", {
        translations: cogniclick.report.translations,
        language: language,
        supportedLanguages: this.supportedLanguages,
      });
    }
    this.reportCookieKey = `ccreport_${this.uid}`;
    this.cookies = new CookieStorage({
      sameSite: "Lax",
      path: "/",
    });
    this.localConfig = this.config;
    if (typeof this.localConfig.theme.headingFont) {
      this.selectedFonts.headingFont = this.localConfig.theme.headingFont;
    }
    if (typeof this.localConfig.theme.copyFont) {
      this.selectedFonts.copyFont = this.localConfig.theme.copyFont;
    }
    this.selectedTheme = this.localConfig.theme.name;
    this.brandColors.backgroundColor = this.localConfig.theme.brandColor;
    this.brandColors.color = this.localConfig.theme.brandTextColor;

    this.report.questions = this.config.questions;
    this.report.stats = this.config.stats;
    this.report.answers = this.answers || this.config.answers;
    this.report.questionSchema = this.config.questionSchema;
    if (this.lead) {
      this.leadUUID = this.lead;
    }

    this.reloadBaseStyles();
    this.reloadBrandStyles();
    window.addEventListener("resize", this.onResize);
    this.onResize();
    window.addEventListener("DOMContentLoaded", this.onDOMReady);
  },
  mounted() {
    ReportEvents.$on("ctaEvent", this.onCTAClick);
    ReportEvents.$on("reorder", this.enableReorder);
    // ReportEvents.$on('reorderEnd', this.onEndReorder)
    let reportCookie = this.cookies.getItem(this.reportCookieKey);

    if (!this.leadUUID && reportCookie && reportCookie !== "undefined") {
      this.leadUUID = reportCookie;
    }
    if (this.published && this.config.signup.force && !this.leadUUID) {
      this.showSignupModal = true;
    } else {
      this.showSignupModal = false;
    }

    if (this.showSignupModal) {
      document.body.classList.add("noscroll");
    }
    // View event here, but only if lead available
    if (this.leadUUID && this.published) {
      this.postViewEvent();
    }
  },
  watch: {
    config: {
      handler: function(newConfig, oldConfig) {
        if (typeof oldConfig === "undefined") {
          return;
        }
        if (this.fontsHaveChanged(newConfig.theme)) {
          this.reloadBrandStyles();
        }
        if (this.brandColoursHaveChanged(newConfig.theme)) {
          this.reloadBrandStyles();
        }
        if (this.themeHasChanged(newConfig.theme)) {
          this.reloadBaseStyles();
        }
      },
      deep: true,
    },
    showSignupModal(showModal) {
      if (showModal === false) {
        document.body.classList.remove("noscroll");
      }
    },
  },
};
</script>
<style lang="scss">
.simple-form
  .lead-form
  input:not([type="radio"]):not([type="checkbox"]):not([type="submit"]):not([type="file"]):not([type="color"]):not(.trix-button):not(.vc-input__input),
#simple-editor select {
  min-width: 0;
  padding-bottom: 0.2rem;
}

.report-editor {
  .modal-bg {
    overflow: scroll !important;
    align-items: flex-start !important;
    padding-top: 70px;
  }
}

body.noscroll {
  overflow: hidden;
}

.locked {
  pointer-events: none;
}

@media (max-width: 768px) and (orientation: landscape) {
  body.noscroll {
    overflow: visible;
  }
}

#report-view {
  .report .component-wrapper {
    z-index: 1;
    position: relative;
  }
  width: 100%;
  padding: 0;
  margin: 0;
  .flex > .content {
    padding: 0rem;
    @media (min-width: 768px) {
      padding: 2rem 1rem 1rem;
    }
    @media (min-width: 1024px) {
      padding: 2rem 1rem 1rem;
    }
  }
  .report-bg > .content {
    background-color: #efefef;
    padding: 0rem;
    @media (min-width: 768px) {
      padding: 2rem 1rem 1rem;
    }
    @media (min-width: 1024px) {
      padding: 2rem 1rem 1rem;
    }
  }
}

.sortable-selected {
  border: 2px dotted blue !important;
  //background-color: #b3c4cc !important;
}
</style>
<style lang="scss" scoped>
.report {
  box-shadow: 0px 0px 10px 5px rgba(0, 0, 0, 0.07);
  width: 100%;
  margin: auto;
  max-width: 960px;
  .report-item {
    width: 100%;
    &.sortable-chosen {
      border: 2px solid blue !important;
    }
    // /deep/ .minimised {
    //   padding: 1rem;
    //   font-size: 20px;
    // }
  }
  .report-item-enter-active,
  .report-item-leave-active {
    transition: all 0.5s;
    background-color: #eef9f9;
  }
  .report-item-enter, .report-item-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
    transform: translateY(100px);
  }
  .topbar {
    height: 50px;
    background-color: transparent;
    border-bottom: none;
  }
}

.component-wrapper.reordering {
}

.modal-bg {
  z-index: 2;
  position: fixed;
  width: 100%;
  height: 100%;
  margin: auto;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: scroll;
}

@media (max-width: 768px) and (orientation: landscape) {
  .anon {
    .component-wrapper {
      //display: none;
    }
    .modal-bg {
      position: absolute;
      padding: 1rem;
      align-items: flex-start;
    }
  }
}

@media (max-height: 500px) {
  .anon {
    .modal-bg {
      align-items: flex-start;
    }
  }
}

.lang-select {
  top: 5px;
  right: 5px;
  margin-left: auto;
  select {
    min-width: 120px;
    background-position: 90% 50%;
  }
}

/deep/ .lead-form {
  background-color: white;
  width: 90%;
  height: auto;
  max-width: 500px;
  max-height: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}
</style>
